import React, { useState, useMemo } from 'react';
import { Button, Label, Input } from '@americanexpress/dls-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { addErrorToReport, addLogToReport } from '@americanexpress/one-app/ducks';
import { getApplicationToken, getEnv } from '../../redux/selectors';
import { setEmail, setPreferenceStatus, setOriginalPreferenceStatus } from '../../redux/privacy/action';
import { isUkMarketPathname, validateEmailRegex, shouldSkipPreferencePageByLocale } from '../../utils/ModuleMap';
import FormattedRichMessage from '../common/FormattedRichMessages';
import IconHoverList from './IconHoverList';
import PrivacyFlowLayout from './PrivacyFlowLayout';
import styles from './styles/PrivacyFlow.scss';
import { postPrivacyPreferenceSearchDetails, forceOptOutForSkipMarkets } from '../../services/privacy.service';
const PrivacyHomepage = () => {
  const [email, setEmailVal] = useState('');
  const [error, setError] = useState('');
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeLocale = intl.locale;
  const {
    pathname
  } = useLocation();
  const appToken = useSelector(getApplicationToken);
  const env = useSelector(getEnv);
  const isUkMarket = useMemo(() => isUkMarketPathname(pathname), [pathname]);
  const isInvalidEmail = () => !validateEmailRegex(email) || !email.trim();
  const reportTokenOrSearchError = async response => {
    await dispatch(addErrorToReport(response.error, response.errorData));
    await dispatch(addLogToReport('warn', 'token-or-search-error', response.errorData));
    console.error('Token/Search API error:', response.errorData);
    setError('Something went wrong. Please try again.');
  };
  const handleSkipMarketFlow = async preferenceStatus => {
    try {
      if (preferenceStatus !== 'O') {
        const updateResponse = await forceOptOutForSkipMarkets({
          emailAddress: email,
          appToken,
          env
        });
        console.log('updateResponse', updateResponse);
        if ((updateResponse === null || updateResponse === void 0 ? void 0 : updateResponse.responseCode) !== 'SUCCESS') {
          setError('Unable to update preferences. Please try again.');
          return;
        }
      }
      navigate('/ipp/confirm');
    } catch (updateError) {
      dispatch(addLogToReport('warn', 'updatePreferenceError', updateError));
      setError('Something went wrong while updating preferences.');
    }
  };
  const handleSearchSuccess = async response => {
    var _response$status, _response$entityPrefe;
    if ((response === null || response === void 0 || (_response$status = response.status) === null || _response$status === void 0 ? void 0 : _response$status.responseCode) !== 'SUCCESS') {
      setError('Unable to fetch preferences. Please try again.');
      return;
    }
    const preferenceStatus = ((_response$entityPrefe = response.entityPreferenceResponse) === null || _response$entityPrefe === void 0 || (_response$entityPrefe = _response$entityPrefe.preferenceCodeDetails) === null || _response$entityPrefe === void 0 || (_response$entityPrefe = _response$entityPrefe[0]) === null || _response$entityPrefe === void 0 ? void 0 : _response$entityPrefe.preferenceStatus) || 'U';
    dispatch(setEmail(email));
    dispatch(setPreferenceStatus(preferenceStatus));
    dispatch(setOriginalPreferenceStatus(preferenceStatus));
    if (shouldSkipPreferencePageByLocale(activeLocale)) {
      await handleSkipMarketFlow(preferenceStatus);
      return;
    }
    navigate('/ipp/preferences');
  };
  const reportUnhandledSearchError = async errorAPI => {
    await dispatch(addLogToReport('warn', 'searchPreferenceError', errorAPI));
    await dispatch(addErrorToReport(errorAPI, {
      emailAddress: email,
      timestamp: new Date().toISOString()
    }));
    console.error('Unhandled search flow error:', errorAPI);
    setError('Something went wrong. Please try again.');
  };
  const handleSubmit = async event => {
    event.preventDefault();
    if (isInvalidEmail()) {
      setError(intl.formatMessage({
        id: 'privacyHomepage.invalidEmail',
        defaultMessage: 'Invalid email address, please try again.'
      }));
      return;
    }
    setError('');
    try {
      // Step 1: Call Search API
      const response = await postPrivacyPreferenceSearchDetails({
        emailAddress: email,
        appToken,
        env
      });
      if (response !== null && response !== void 0 && response.error) {
        await reportTokenOrSearchError(response);
        return;
      }
      await handleSearchSuccess(response);
    } catch (errorAPI) {
      await reportUnhandledSearchError(errorAPI);
    }
  };
  const groupedNotes = useMemo(() => Object.keys(intl.messages).filter(key => key.startsWith('privacyHomepage.note')).sort().reduce((acc, key) => {
    const match = key.match(/privacyHomepage\.note(\d+)([a-z]?)/);
    if (!match) return acc;
    const [, mainIndex, subIndex] = match;
    if (!acc[mainIndex]) {
      acc[mainIndex] = {
        main: null,
        children: []
      };
    }
    if (subIndex) {
      acc[mainIndex].children.push(key);
    } else {
      acc[mainIndex].main = key;
    }
    return acc;
  }, {}), [intl.messages]);
  return /*#__PURE__*/React.createElement(PrivacyFlowLayout, null, /*#__PURE__*/React.createElement("h1", {
    className: `${styles.title} color-text-brand-alt`
  }, /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: "privacyHomepage.title"
  })), intl.messages['privacyHomepage.noteFr'] && /*#__PURE__*/React.createElement("div", {
    className: styles.noteFr
  }, /*#__PURE__*/React.createElement("p", {
    className: styles.fr_text
  }, /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: "privacyHomepage.noteFr"
  })), /*#__PURE__*/React.createElement("ul", null, /*#__PURE__*/React.createElement("li", {
    className: styles.fr_textLi
  }, /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: "privacyHomepage.noteFr1"
  })), /*#__PURE__*/React.createElement("li", {
    className: styles.fr_textLi
  }, /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: "privacyHomepage.noteFr2"
  })))), /*#__PURE__*/React.createElement("p", {
    className: styles.text
  }, /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: "privacyHomepage.pageDescription"
  })), isUkMarket && /*#__PURE__*/React.createElement(IconHoverList, null), /*#__PURE__*/React.createElement("form", {
    className: styles.form,
    onSubmit: handleSubmit,
    noValidate: true
  }, /*#__PURE__*/React.createElement("div", {
    className: `${styles.fieldGroup} ${isUkMarket ? styles.ukFieldGroup : ''}`
  }, intl.messages['privacyHomepage.emailLabel'] && /*#__PURE__*/React.createElement(Label, {
    htmlFor: "email",
    className: styles.emailLabel
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "privacyHomepage.emailLabel"
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.formRow
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.inputContainer
  }, /*#__PURE__*/React.createElement(Input, {
    type: "email",
    id: "email",
    lang: "en-US",
    "aria-describedby": "label-id",
    value: email,
    onChange: e => {
      setEmailVal(e.target.value);
      if (error) setError('');
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.buttonContainer
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "primary",
    className: styles.privacy_btn
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "continue"
  })))), error && /*#__PURE__*/React.createElement("div", {
    className: styles.errorMessage
  }, error))), intl.messages.pleaseNote && /*#__PURE__*/React.createElement("h2", {
    className: styles.subtitle
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "pleaseNote"
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.note
  }, /*#__PURE__*/React.createElement("ul", null, Object.values(groupedNotes).map(group => /*#__PURE__*/React.createElement("li", {
    key: group === null || group === void 0 ? void 0 : group.main
  }, group.main && /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: group.main
  }), group.children.length > 0 && /*#__PURE__*/React.createElement("ol", null, group.children.map(childKey => /*#__PURE__*/React.createElement("li", {
    key: childKey
  }, /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: childKey
  }))))))), /*#__PURE__*/React.createElement("br", null), intl.messages.conclusion && /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: "conclusion"
  })), intl.messages['privacyHomepage.additionalInfo'] && /*#__PURE__*/React.createElement("p", {
    className: styles.text
  }, /*#__PURE__*/React.createElement(FormattedRichMessage, {
    id: "privacyHomepage.additionalInfo"
  })));
};
export default PrivacyHomepage;